import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useStore } from '../store/store'
import { behaviorStats, dashboardSummary, equityCurve } from '../store/selectors'
import { evaluateBadges } from '../lib/behavior'
import { fmtCurrency, fmtPct, fmtPctValue, fmtSignedCurrency, pnlClass } from '../lib/format'
import { Card, PageHead, Pill, Stat } from '../components/ui'
import { Sparkline } from '../components/charts'
import { BadgeShelf, DegenerateBanner, GreedMeterCard, OpportunityCard, WarningList } from '../components/widgets'

export function Dashboard() {
  const store = useStore()
  const summary = useMemo(() => dashboardSummary(store), [store])
  const curve = useMemo(() => equityCurve(store), [store])
  const badges = useMemo(() => evaluateBadges(behaviorStats(store)), [store])
  const currency = store.risk.baseCurrency

  return (
    <div className="stack">
      <PageHead title="Dashboard">
        Your bankroll, open risk, edges and behavioral guardrails at a glance. Research and paper-trading only — nothing here is a guaranteed win.
      </PageHead>

      <div className="grid cols-4">
        <Stat label="Bankroll" value={fmtCurrency(summary.bankroll, currency)} sub={`P/L ${fmtSignedCurrency(summary.totalPnl, currency)}`} tone={summary.totalPnl >= 0 ? 'pos' : 'neg'} />
        <Stat label="Win rate" value={fmtPct(summary.winRate, 0)} sub={`${summary.journal.wins}W / ${summary.journal.losses}L resolved`} />
        <Stat label="Calibration" value={summary.calibrationScore.toFixed(0)} sub="0–100, higher = better calibrated" />
        <Stat label="Max drawdown" value={fmtPctValue(summary.maxDrawdownPct)} sub={`now ${fmtPctValue(summary.currentDrawdownPct)}`} tone="neg" />
      </div>

      <div className="grid cols-4">
        <Stat label="Daily risk used" value={fmtPctValue(summary.dailyRiskUsedPct, 0)} sub={`of ${fmtPct(store.risk.maxDailyLossPct, 0)} loss budget`} tone={summary.dailyRiskUsedPct > 70 ? 'neg' : undefined} />
        <Stat label="Open positions" value={String(summary.openPositions)} sub={`${fmtCurrency(summary.openExposure, currency)} exposure`} />
        <Stat label="Exposure" value={fmtPctValue(summary.openExposurePct, 0)} sub="of bankroll deployed" />
        <Stat label="Realized today" value={fmtSignedCurrency(summary.realizedPnlToday, currency)} tone={summary.realizedPnlToday >= 0 ? 'pos' : 'neg'} />
      </div>

      <div className="grid cols-3">
        <div className="stack" style={{ gridColumn: 'span 2' }}>
          <Card title="Equity curve" subtitle="Bankroll over the last 30 snapshots">
            <Sparkline values={curve} color="auto" height={90} />
            <div className="row between" style={{ marginTop: 8 }}>
              <span className="muted">Start {fmtCurrency(curve[0] ?? 0, currency)}</span>
              <span className={`mono ${pnlClass(summary.totalPnl)}`}>{fmtSignedCurrency(summary.totalPnl, currency)}</span>
            </div>
          </Card>

          <Card title="Risk alerts" subtitle="Auto-generated from your limits and behavior">
            <WarningList warnings={summary.warnings} />
          </Card>

          <DegenerateBanner verdict={summary.degenerate} />
        </div>

        <GreedMeterCard greed={summary.greed} />
      </div>

      <Card
        title="Best current opportunities"
        subtitle="Ranked by EV, confidence, liquidity and risk — skips excluded"
        action={<Link to="/scanner" className="btn sm">Open scanner</Link>}
      >
        {summary.bestOpportunities.length === 0 ? (
          <div className="empty">
            No positive-edge opportunities right now. <strong>No trade is a position.</strong> Add a model probability on the Markets, Odds or Model Lab pages.
          </div>
        ) : (
          <div className="grid cols-2">
            {summary.bestOpportunities.map((opp) => (
              <OpportunityCard key={opp.input.id} opp={opp} />
            ))}
          </div>
        )}
      </Card>

      <Card title="Achievements" subtitle="Rewards for good process, not for gambling more">
        <BadgeShelf badges={badges} />
      </Card>

      <div className="row between muted" style={{ fontSize: 12 }}>
        <span>EdgeLab v{store.meta.version} · jurisdiction: {store.meta.jurisdiction}</span>
        <Pill tone="gray">Not financial or betting advice</Pill>
      </div>
    </div>
  )
}
